/**
 * InAppFeedbackRemapper
 *
 * Bridges raw Firestore JSON → InAppFeedbackDto → InAppFeedbackEntity,
 * following the `fromJson(): Dto` + `fromDto(): Entity` + list-variant
 * convention codified by `campaignRemapper.ts` / `appNotificationRemapper.ts`
 * and `data-layer.md` → "Remappers".
 *
 * Flutter source:
 *   - lib/data/models/in_app_feedback.dart (+ in_app_feedback.g.dart:9-17)
 *   - lib/data/models/in_app_feedback_screenshot.dart
 *     (+ in_app_feedback_screenshot.g.dart:9-12)
 *
 * `fromJson` parses defensively to guard partial documents:
 *   - `id` / `userId` / `description` default to `''`, `timestamp` defaults to
 *     `0` (required fields in Flutter's generated `fromJson`, so defaulting only
 *     hardens against malformed/partial docs without changing valid-doc parsing).
 *   - `screenshots` is mapped only when the array is present, mirroring Flutter's
 *     null-safe `(json['screenshots'] as List?)?.map(...)` — a missing array stays
 *     `undefined`, never `[]`.
 *   - each screenshot's `path` defaults to `''`; `aspectRatio` stays `undefined`
 *     when absent (Flutter `double?` → `(json['aspectRatio'] as num?)?.toDouble()`).
 *
 * The nested screenshot shape is mapped through dedicated sub-helpers (never
 * copied field-by-field at the call site) so it can never drift from
 * `InAppFeedbackScreenshotDto` / `InAppFeedbackScreenshotEntity`.
 *
 * `toJson` is the write-direction serializer used by the add-feedback save
 * path, mirroring Flutter's generated `_$InAppFeedbackToJson`
 * (in_app_feedback.g.dart:19-36) + `_$InAppFeedbackScreenshotToJson`
 * (in_app_feedback_screenshot.g.dart:14-28). Both models are
 * `@JsonSerializable(includeIfNull: false)` with no `@JsonKey` renames, so:
 *   - `id` / `userId` / `description` / `timestamp` (and screenshot `path`) are
 *     always emitted;
 *   - `screenshots` is emitted only when non-null (`writeNotNull`,
 *     in_app_feedback.g.dart:32-33) — a missing array stays **absent**, never
 *     `[]` or `null`;
 *   - screenshot `aspectRatio` is emitted only when non-null (`writeNotNull`,
 *     in_app_feedback_screenshot.g.dart:26) — absent, never `null`.
 */
import type { InAppFeedbackDto } from '@/data/dtos/feedback/inAppFeedbackDto';
import type { InAppFeedbackScreenshotDto } from '@/data/dtos/feedback/inAppFeedbackScreenshotDto';
import type { JsonObject } from '@/data/types/json';
import type {
  InAppFeedbackEntity,
  InAppFeedbackScreenshotEntity,
} from '@/domain/entities/feedback/inAppFeedbackEntity';

export class InAppFeedbackRemapper {
  private static screenshotFromJson(
    data: JsonObject,
  ): InAppFeedbackScreenshotDto {
    return {
      path: (data['path'] as string | undefined) ?? '',
      aspectRatio: data['aspectRatio'] as number | undefined,
    };
  }

  /** Parse a Firestore `in_app_feedback` document into an `InAppFeedbackDto`. */
  static fromJson(data: JsonObject): InAppFeedbackDto {
    const rawScreenshots = data['screenshots'] as JsonObject[] | undefined;
    return {
      id: (data['id'] as string | undefined) ?? '',
      userId: (data['userId'] as string | undefined) ?? '',
      description: (data['description'] as string | undefined) ?? '',
      screenshots: rawScreenshots?.map(InAppFeedbackRemapper.screenshotFromJson),
      timestamp: (data['timestamp'] as number | undefined) ?? 0,
    };
  }

  static fromJsonList(dataList: JsonObject[]): InAppFeedbackDto[] {
    return dataList.map(InAppFeedbackRemapper.fromJson);
  }

  private static screenshotFromDto(
    dto: InAppFeedbackScreenshotDto,
  ): InAppFeedbackScreenshotEntity {
    return {
      path: dto.path,
      aspectRatio: dto.aspectRatio,
    };
  }

  /** Build an `InAppFeedbackEntity` from an `InAppFeedbackDto`. */
  static fromDto(dto: InAppFeedbackDto): InAppFeedbackEntity {
    return {
      id: dto.id,
      userId: dto.userId,
      description: dto.description,
      screenshots: dto.screenshots?.map(InAppFeedbackRemapper.screenshotFromDto),
      timestamp: dto.timestamp,
    };
  }

  static fromDtoList(dtoList: InAppFeedbackDto[]): InAppFeedbackEntity[] {
    return dtoList.map(InAppFeedbackRemapper.fromDto);
  }

  /**
   * Serialize one screenshot for the Firestore `screenshots` array, mirroring
   * `_$InAppFeedbackScreenshotToJson` (in_app_feedback_screenshot.g.dart:14-28):
   * always emit `path`; emit `aspectRatio` only when non-null (`writeNotNull`,
   * :26).
   */
  private static screenshotToJson(
    dto: InAppFeedbackScreenshotDto,
  ): JsonObject {
    const json: JsonObject = {
      path: dto.path,
    };
    if (dto.aspectRatio != null) {
      json['aspectRatio'] = dto.aspectRatio;
    }
    return json;
  }

  /**
   * Serialize an `InAppFeedbackDto` to the Firestore `in_app_feedback` document
   * shape, mirroring `_$InAppFeedbackToJson` (in_app_feedback.g.dart:19-36):
   * always emit `id` / `userId` / `description` / `timestamp`; emit
   * `screenshots` only when non-null (`writeNotNull`, :32-33). A missing array
   * stays absent (`includeIfNull:false`), never `[]` or `null`.
   */
  static toJson(dto: InAppFeedbackDto): JsonObject {
    const json: JsonObject = {
      id: dto.id,
      userId: dto.userId,
      description: dto.description,
      timestamp: dto.timestamp,
    };
    if (dto.screenshots != null) {
      json['screenshots'] = dto.screenshots.map(
        InAppFeedbackRemapper.screenshotToJson,
      );
    }
    return json;
  }
}
